import { useMemo } from 'react'
import {
  BedConfigurations,
  PickupType,
  SmokingPreference,
  BedType,
  DomesticRatePlanBenefits,
} from '~/__generated__/__types__'
import { AccommodationDetailsQuery } from '~/__generated__/app/query/order-details/AccommodationDetails.graphql'
import { getDomesticRateplanBenefits } from '~/app/utils/domesticRateplan'
import { isTruthy } from '~/app/utils'

/**
 * useExtractAccommodationDetailsResponse
 * author: soojin.oh
 */

// type definitions

const BedName: { [K in BedType]: string } = {
  [BedType.CribBed]: '유아용 침대',
  [BedType.DoubleBed]: '더블 침대',
  [BedType.KingBed]: '킹 침대',
  [BedType.QueenBed]: '퀸 침대',
  [BedType.QueenSofaBed]: '소파베드 (2인용)',
  [BedType.SingleBed]: '싱글 침대',
  [BedType.SingleBunkBed]: '이층 침대',
  [BedType.SingleFuton]: '요이불',
  [BedType.SingleSofaBed]: '소파베드 (1인용)',
  [BedType.SingleWaterBed]: '물침대',
  [BedType.SuperSingleBed]: '슈퍼싱글 침대',
}

type Response = Partial<Pick<AccommodationDetailsQuery, 'roomType' | 'property'>> & {
  ratePlanTitle?: string
  benefits?: DomesticRatePlanBenefits[]
  ratePlanDescription?: string
}
// hook
const useExtractDetailsResponse = (response?: Response | null) => {
  const extracted = useMemo(() => {
    if (!response?.roomType || !response?.property) {
      return null
    }
    const { property, roomType, ratePlanTitle, benefits, ratePlanDescription } = response
    const smoking = roomType.data.smokingPreference === SmokingPreference.Smoking ? '흡연' : '금연'
    const direction = property.data.descriptions?.direction?.split('\n').filter(v => !!v.trim())
    const pickup =
      property.data.descriptions?.pickupType !== PickupType.NotProvided
        ? property.data.descriptions?.pickup?.split('\n').filter(v => !!v.trim())
        : []
    const notices = property.data.notices?.information?.map(info => ({
      title: info?.title,
      contents: info?.content?.split('\n').filter(v => !!v.trim()),
    }))
    const capacity = `기본 ${roomType.data.capacity}인 / 최대 ${roomType.data.maxCapacity}인`
    const toBedText = (configure?: BedConfigurations | null) => {
      if (configure && configure.bedType) {
        return `${BedName[configure.bedType]} ${configure.quantity}개`
      }
      return null
    }
    const bed = response.roomType.data.bedGroups
      ?.map(bed => bed?.bedConfigurations?.map(toBedText).join(', '))
      .join(' 또는 ')
    const themes = response.property.data.themesImages?.map(({ name, photos }) => ({
      name,
      photo: photos?.unavailable,
    }))
    const { breakfast, checkIn, checkOut } = getDomesticRateplanBenefits(benefits)
    const ratePlanBenefits = [breakfast, checkIn, checkOut].filter(isTruthy)

    return {
      hostName: property.data.name,
      address: property.data.address || '',
      phone: property.data.phone,
      customerServicePhone: property.data.customerServicePhone,
      coordinate: {
        lat: property.data.coordinate?.latitude,
        lng: property.data.coordinate?.longitude,
      },
      roomName: roomType.data.name,
      capacity,
      smoking,
      notices,
      themes,
      direction,
      pickup,
      bed,
      description: ratePlanDescription,
      ratePlanTitle,
      ratePlanBenefits,
    }
  }, [response])
  return extracted
}

export default useExtractDetailsResponse
